import { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../../../hooks";
import { setShowFailedMessageThunk } from "../../../global/globalSlice";
import {
  usePostProjectByProjectIdReportboardsAndReportIdFieldMutation,
  usePostProjectByProjectIdReportboardsAndReportIdCommentMutation,
} from "./reportapi.enhance";
import type {
  PostProjectByProjectIdReportboardsAndReportIdFieldApiResponse as DirectReportResult,
  PostProjectByProjectIdReportboardsAndReportIdCommentApiArg,
} from "../../../../openapi";

export type ReportCommentOperation = Omit<
  PostProjectByProjectIdReportboardsAndReportIdCommentApiArg["body"],
  "expectedRevision"
>;

export type DirectReportActions = {
  saving: boolean;
  disabled?: boolean;
  updateField: (path: string, value: string | number | boolean | null) => Promise<void>;
  updateComment: (operation: ReportCommentOperation) => Promise<void>;
};

export const useDirectReportSave = (options: {
  enabled: boolean;
  projectId: string;
  reportId: string;
  revision: number;
  online: boolean;
}) => {
  const dispatch = useAppDispatch();
  const [saveField] = usePostProjectByProjectIdReportboardsAndReportIdFieldMutation();
  const [saveComment] = usePostProjectByProjectIdReportboardsAndReportIdCommentMutation();
  const latest = useRef(options);
  latest.current = options;
  const mounted = useRef(true);
  const pending = useRef(0);
  const blocked = useRef(false);
  const retryAction = useRef<(() => Promise<void>) | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = (
    send: (expectedRevision: number) => Promise<DirectReportResult>,
    field?: { retry: () => Promise<void> },
  ): Promise<void> => {
    pending.current++;
    setSaving(true);
    const task = (async () => {
      try {
        if (!mounted.current) throw new Error("Rapporten er lukket");
        if (!latest.current.enabled) throw new Error("Rapporten bruker ikke direktelagring");
        if (blocked.current) throw new Error("Last inn rapporten på nytt før du fortsetter.");
        if (!latest.current.online) throw new Error("Du er offline. Koble til internett og prøv igjen.");
        const result = await send(latest.current.revision);
        if (mounted.current && !retryAction.current) setError(null);
        return result;
      } catch (cause) {
        if (mounted.current) {
          const isConflict = cause?.status === 409 || cause?.data?.code === "CONFLICT" || blocked.current;
          blocked.current = isConflict;
          setConflict(isConflict);
          const message = cause?.data?.message ?? cause?.message ?? "Kunne ikke lagre. Prøv igjen.";
          setError(message);
          dispatch(setShowFailedMessageThunk(message));
          if (isConflict) retryAction.current = null;
          else if (field && !retryAction.current) retryAction.current = field.retry;
        }
        throw cause;
      } finally {
        pending.current--;
        if (mounted.current) setSaving(pending.current > 0);
      }
    })();
    return task.then(() => undefined);
  };

  const updateField = (path: string, value: string | number | boolean | null): Promise<void> => {
    if (blocked.current) return Promise.reject(new Error("Last inn rapporten på nytt før du fortsetter."));
    return run(
      (expectedRevision) =>
        saveField({
          projectId: options.projectId,
          reportId: options.reportId,
          body: { path, value, expectedRevision },
        }).unwrap(),
      { retry: () => updateField(path, value) },
    );
  };

  return {
    saving,
    error,
    conflict,
    disabled: conflict || retryAction.current != null,
    retry: retryAction.current
      ? () => {
          const retry = retryAction.current;
          retryAction.current = null;
          void retry?.().catch(() => undefined);
        }
      : null,
    updateField,
    updateComment: (operation: ReportCommentOperation) =>
      run((expectedRevision) =>
        saveComment({
          projectId: options.projectId,
          reportId: options.reportId,
          body: { ...operation, expectedRevision },
        }).unwrap(),
      ),
  };
};
